import { Component, OnDestroy, OnInit, ViewChild, ElementRef, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import * as moment from 'moment';

import { LoginService } from '@intranet/modules/login/login.service';
import { TogglService, RecentTimerEvent } from '@intranet/modules/toggl';
import { Subscription, Observable } from 'rxjs';
import { FormControl } from '@angular/forms';
import { startWith, map, distinctUntilChanged, filter, take } from 'rxjs/operators';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

export interface TogglJob {
    id: number;
    title: string;
    jobNumber: string;
}

@Component({
    selector: 'header-component',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
    @ViewChild('togglContainer', { read: ElementRef }) togglContainer!: ElementRef;

    constructor(
        public loginService: LoginService,
        public router: Router,
        public togglService: TogglService
    ) {}

    ngOnDestroy(): void {
        this.togglJobSub?.unsubscribe();
        this.saveSub?.unsubscribe();
    }

    showTogglHeader: boolean = true;
    displayName: string;
    showTogglLogin: boolean = false;
    togglUsername: string = '';
    togglPassword: string = '';
    loginFailed: boolean = false;
    isTogglTimerRunning: boolean = false;
    jobTitle: string = '';
    jobId: number = 0;
    jobNumber: number = 0;
    description: string = '';
    clientName: string = '';
    timerStartedAt: moment.Moment | null = null;
    lastPolledAt: moment.Moment | null = null;
    isPolling: boolean = false;
    lastJob: TogglJob | null = null;
    private togglJobSub!: Subscription;
    descCtrl = new FormControl('');
    recentDescriptions: string[] = [];
    editingDescription = false;
    saveSub?: Subscription;
    holdOpen = false;
    filteredDescriptions$!: Observable<string[]>;
    recentEvents: RecentTimerEvent[] = [];
    errorText: string | null = null;

      ngOnInit() {
          this.togglService.isLoggedIn$
            .pipe(
                filter(loggedIn => loggedIn),
                take(1)
            )
            .subscribe(() => {
              this.loadHistory();

              // Subscribe to the running job observable
              this.togglJobSub = this.togglService.togglRunningJob$
                .pipe(
                    distinctUntilChanged((prev, curr) =>
                        prev?.jobId === curr?.jobId &&
                        prev?.description === curr?.description
                    )
                )
                .subscribe(runningJob => {
                  if (runningJob) {
                      this.jobTitle = runningJob.jobTitle;
                      this.jobId = runningJob.jobId;
                      this.jobNumber = runningJob.jobNumber;
                      this.clientName = runningJob.clientName;
                      this.description = runningJob.description || '';
                      this.isTogglTimerRunning = true;
                      this.timerStartedAt = moment(runningJob.startTime);
                      this.lastJob = {
                          id: runningJob.jobId,
                          title: runningJob.jobTitle,
                          jobNumber: runningJob.jobNumber.toString()
                      };
                    } else {
                        this.isTogglTimerRunning = false;
                        this.timerStartedAt      = null;
                    }

                    this.loadHistory();
                });
            });

          // grab a cached last‐job right away (this doesn’t hit the API)
          this.displayName = this.getUser();
          this.lastJob = this.togglService.getLastJob();
    }

    resumeLastJob() {
        if (!this.lastJob) { return; }
        this.togglService.resumeLastJob().subscribe(result => {
            if (!result.success) {
                console.error('Couldn’t resume:', result.error);
            } else {
                this.loadHistory();
            }
        });
    }

    runningTimerDuration(): any {
        if (this.timerStartedAt) {
            let duration = moment.duration(moment().diff(this.timerStartedAt));
            return duration.hours().toString() + ':' +
                duration.minutes().toString().padStart(2, '0') + ':' +
                duration.seconds().toString().padStart(2, '0');
        }
        return '';
    }

    stopTimer() {
        console.log('Stopping Toggl timer for job:', this.jobId);
        this.togglService.stopTimer().subscribe({
            next: (result) => {
                if (result.success) {
                    this.isTogglTimerRunning = false;
                    this.timerStartedAt = null;
                    this.loadHistory();
                } else {
                    console.error('Error stopping timer:', result.error);
                }
            },
            error: (error) => {
                console.error('Error stopping timer:', error);
            }
        });
    }

    getUser() {
        let username = this.loginService.getLoggedInAdUserDisplayName();
        return username.substr(0, username.indexOf(' '));
    }

    openTogglLogin() {
        this.loginFailed = false;
        this.errorText = null;
        this.showTogglLogin = true;
    }

    logout() {
        this.loginService.logoutAzure();
    }

    doTogglLogin() {
        this.errorText = null; // reset any previous message
        this.togglService.login(this.togglUsername, this.togglPassword).subscribe((r) => {
            this.loginFailed = !r.result;

            if (r.result) {
                this.showTogglLogin = false;
                this.togglUsername = '';
                this.togglPassword = '';
                return;
            }

            // Message if Toggl is disabled
            if (r.message === 'Toggl is disabled') {
                this.loginFailed = false; // hide the "wrong password" banner
                this.errorText = 'Toggl is temporarily disabled in the Intranet.';
            }
        });
    }


    // Load recent timer events
    loadHistory() {
        this.togglService.getRecentTimerEvents().pipe(
            map(events => events.slice(0, 10))
        ).subscribe(
            events => this.recentEvents = events,
            err => console.error('history error', err)
        );
    }

    // Start timer from a recent event
    startFromEvent(evt: RecentTimerEvent) {
        this.togglService.startTimerFromEvent(evt)
            .subscribe(res => {
                if (res.success) {
                    this.loadHistory();
                }
            });
      }

    private _filter(val: string): string[] {
        const filterValue = val.toLowerCase();
        return this.recentDescriptions.filter(opt =>
            opt.toLowerCase().includes(filterValue)
        );
    }

    startEditingDescription() {
        this.editingDescription = true;
        this.descCtrl.setValue(this.description, { emitEvent: false });
        this.togglService.getRecentTimerDescriptions(this.jobId)
            .subscribe(r => {
                this.recentDescriptions = r.descriptions || [];
                this.filteredDescriptions$ = this.descCtrl.valueChanges.pipe(
                    startWith(this.description),
                    map(val => this._filter(val))
                );
            });
    }

    saveDescription() {
        const newDesc = (this.descCtrl.value || '').trim();
        if (newDesc !== this.description) {
            this.togglService
              .updateCurrentTimerDescription(newDesc)
              .subscribe(res => {
                  if (res.success && res.runningJob) {
                      this.description = res.runningJob.description || '';
                  }
              });
        }

        this.editingDescription = false;
        this.holdOpen = true;
        setTimeout(() => this.holdOpen = false, 900);
    }

    onDescSelected(e: MatAutocompleteSelectedEvent) {
        this.descCtrl.setValue(e.option.value, { emitEvent: false });
    }


    @HostListener('document:click', ['$event.target'])
    onClickOutside(target: HTMLElement) {
        const clickedInsideTimer = this.togglContainer
            && this.togglContainer.nativeElement.contains(target);

        const clickedInAutocomplete = !!target.closest('.mat-autocomplete-panel');

        if (this.editingDescription && !clickedInsideTimer && !clickedInAutocomplete) {
            this.editingDescription = false;
        }
    }

}
